import type { Feature } from '@/enum/global.enum'

/**
 * Onboarding module names as returned by the API (organisation-module list).
 * Used to map which Features are enabled when a module is selected.
 */
export const ORGANISATION_MODULE_NAME = {
  POLICY: 'policy',
  TASK: 'project_management',
  MEETING_MINUTES: 'meeting_minutes',
  SERVICE_PROVIDER: 'service_provider',
  STAFF_REGISTER: 'staff_register',
  DOCUMENT_REPOSITORY: 'document_repository',
  REGULATORY_REGISTER: 'regulatory_register',
  CHANGE_MANAGEMENT: 'change_management',
  INFORMATION_SECURITY_OBJECTIVES: 'information_security_objectives',
  ISMS_CALENDAR: 'isms_calendar',
  SECURITY_AWARENESS: 'security_awareness',
  MANAGE_AI_REGISTER: 'manage_ai_register',
  RISK: 'risk',
  MANAGE_ASSETS: 'manage_assets',
  INCIDENT: 'incident',
  VULNERABILITY: 'vulnerability',
  CYBER_THREAT_RISK_ASSESSMENT: 'cyber_threat_risk_assessment',
  STANDARDS: 'standards',
  REPORT_LIBRARY: 'report_library',
  QUARANTINE_MANAGEMENT: 'quarantine_management',
  NON_CONFORMITY: 'non_conformity',
} as const

export type OrganisationModuleName = (typeof ORGANISATION_MODULE_NAME)[keyof typeof ORGANISATION_MODULE_NAME]

const ORGANISATION_MODULE_NAME_VALUES = new Set<string>(Object.values(ORGANISATION_MODULE_NAME))

/**
 * Narrows an API/runtime-sourced module name string to the known enum, so
 * internal lookups keyed by OrganisationModuleName can be indexed safely.
 */
export function isOrganisationModuleName(name: string): name is OrganisationModuleName {
  return ORGANISATION_MODULE_NAME_VALUES.has(name)
}

/**
 * Maps each selectable onboarding module to the Features it enables.
 * When an org has a module selected, these features become accessible.
 */
export const MODULE_NAME_TO_FEATURES: Partial<Record<OrganisationModuleName, Feature[]>> = {
  [ORGANISATION_MODULE_NAME.POLICY]: ['POLICY', 'EXEMPTIONS_REGISTER'],
  [ORGANISATION_MODULE_NAME.TASK]: ['PROJECT'],
  [ORGANISATION_MODULE_NAME.MEETING_MINUTES]: ['MEETING_MINUTES'],
  [ORGANISATION_MODULE_NAME.SERVICE_PROVIDER]: ['SERVICE_PROVIDER'],
  [ORGANISATION_MODULE_NAME.STAFF_REGISTER]: ['STAFF'],
  [ORGANISATION_MODULE_NAME.DOCUMENT_REPOSITORY]: ['DOCUMENT_REPOSITORY'],
  [ORGANISATION_MODULE_NAME.REGULATORY_REGISTER]: ['COUNTRY_REGULATORY_REGISTER'],
  [ORGANISATION_MODULE_NAME.CHANGE_MANAGEMENT]: ['CHANGE_MANAGEMENT_PROCESS'],
  [ORGANISATION_MODULE_NAME.INFORMATION_SECURITY_OBJECTIVES]: ['INFORMATION_SECURITY_OBJECTIVE'],
  [ORGANISATION_MODULE_NAME.ISMS_CALENDAR]: ['ISMS_ASSURANCE_CALENDAR'],
  [ORGANISATION_MODULE_NAME.SECURITY_AWARENESS]: ['PHISHED', 'MIMECAST'],
  [ORGANISATION_MODULE_NAME.MANAGE_AI_REGISTER]: ['AI_REGISTER'],
  [ORGANISATION_MODULE_NAME.RISK]: [
    'RISK',
    'INFORMATION_ASSET',
    'INCIDENT_REGISTER',
    'RUN_ZERO_ASSET',
    'RISK_ASSESSMENT',
  ],
  [ORGANISATION_MODULE_NAME.CYBER_THREAT_RISK_ASSESSMENT]: ['CTRA'],
  [ORGANISATION_MODULE_NAME.STANDARDS]: ['ASSESSMENTS', 'REPORT_LIBRARY'],
  [ORGANISATION_MODULE_NAME.NON_CONFORMITY]: ['ASSESSMENTS'],
}

/**
 * Reverse map: which module name(s) must be enabled for a feature to be accessible.
 * Features not in this map are not gated by onboarding module selection.
 */
const FEATURE_TO_MODULE_NAMES = ((): Partial<Record<Feature, OrganisationModuleName[]>> => {
  const map: Partial<Record<Feature, OrganisationModuleName[]>> = {}
  for (const [moduleName, features] of Object.entries(MODULE_NAME_TO_FEATURES) as [
    OrganisationModuleName,
    Feature[],
  ][]) {
    for (const feature of features) {
      if (!map[feature]) map[feature] = []
      if (!map[feature]!.includes(moduleName)) map[feature]!.push(moduleName)
    }
  }
  return map
})()

/**
 * Returns whether a feature is gated by organisation module selection.
 */
export function isFeatureGatedByOrganisationModule(feature: Feature | undefined): boolean {
  if (!feature) return false
  return feature in FEATURE_TO_MODULE_NAMES
}

/**
 * Returns whether a feature is allowed given the list of enabled organisation module names.
 */
export function isFeatureAllowedByOrganisationModules(
  feature: Feature | undefined,
  enabledModuleNames: string[]
): boolean {
  if (!feature) return true
  const requiredModules = FEATURE_TO_MODULE_NAMES[feature]
  if (!requiredModules || requiredModules.length === 0) return true
  return requiredModules.some((name) => enabledModuleNames.includes(name))
}
